/* ExposureNavMemory.js */

// Where the operator was in the exposure list, remembered across view teardown.
//
// Every game-editor view is destroyed and rebuilt on navigation (setActiveView shuts the outgoing
// view down and constructs the incoming one), so nothing an instance holds can survive a trip into
// the per-CVE drawer and back. Reviewing exposure means reading one CVE, returning to the list, and
// picking up the next one — and on a device with forty-odd verdicts, landing back at row 1 every
// time makes the list unusable past the first screen.
//
// Module scope is deliberate: it is the only place that outlives the views, and this is view
// position rather than user preference, so it must NOT reach the persistent user-data storage —
// restoring a scroll offset captured last week would be worse than starting at the top.
//
// Keyed by device id so two device editors open side by side keep their own place. Bounded to
// MAX_DEVICES entries, evicting the least recently WRITTEN, because nothing ever tells this module
// that an editor closed. (Reads do not refresh recency: only remember() re-inserts.)

const MAX_DEVICES = 25;

// deviceId -> {scrollTop: Number, focusCveId: String|null, order: String[]}
const memory = new Map();

const EMPTY = {scrollTop: 0, focusCveId: null, order: []};

/**
 * Merges `patch` into the device's remembered position, leaving untouched fields alone — the table
 * records scroll offsets while the drawer records which CVE is being read, and neither should erase
 * the other's field.
 *
 * @param {String} deviceId Ignored when falsy, so a caller that could not resolve the device is a
 *   no-op rather than a shared "undefined" bucket every device would then restore from.
 * @param {Object} patch Any subset of {scrollTop, focusCveId, order}.
 */
function remember(deviceId, patch) {
    if (!deviceId || !patch) {
        return;
    }
    const existing = memory.get(deviceId);
    // Re-insert so Map iteration order stays least-recently-used first for the eviction below.
    if (existing) {
        memory.delete(deviceId);
    }
    const merged = Object.assign({}, EMPTY, existing, patch);
    // Copy the array rather than storing whatever reference we were handed. Object.assign is a
    // SHALLOW merge, so every device remembered without an explicit `order` — which is most of
    // them, since the table writes scroll offsets and the drawer writes a focused CVE — would
    // otherwise store EMPTY.order, the module's ONE array instance. A single push by any caller
    // would then be visible from every device, which is exactly the per-device isolation this
    // module exists to provide.
    merged.order = (merged.order || []).slice();
    memory.set(deviceId, merged);
    if (memory.size > MAX_DEVICES) {
        memory.delete(memory.keys().next().value);
    }
}

/**
 * The device's remembered position. Always returns an object — a device with no history reads as
 * "top of the list, nothing viewed", which is exactly the first-visit behaviour.
 *
 * @param {String} deviceId
 * @returns {{scrollTop: Number, focusCveId: String|null, order: String[]}} A copy; mutating it
 *   does not write back.
 */
function recall(deviceId) {
    const out = Object.assign({}, EMPTY, (deviceId && memory.get(deviceId)) || {});
    // Copy again on the way out, so the JSDoc above is true: a caller that sorts or splices the
    // returned order must not be editing what the next caller reads.
    out.order = out.order.slice();
    return out;
}

module.exports = {
    remember,
    recall,
};
